import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import {
  getNetworkFromVertex,
  chatWithKrakenStream,
  expandNodeStream,
  discoverWebPresenceStream,
  createEntityFromNodeStream,
  type CreateEntityFromNodeOpts,
  applyEntitySuggestions,
  getClusterList,
  getClusterDetail,
  postCluster,
  patchCluster,
  deleteCluster,
  expandNode,
} from '../api/react/kraken/kraken'
import type { KrakenStreamEvent, KrakenExpandEvent, KrakenDiscoverEvent, KrakenCreateEntityEvent, KrakenEntitySuggestion, KrakenEntityUpdateType, KrakenChatRequest, ClusterListParams, CreateClusterPayload, UpdateClusterPayload } from '../types/kraken.types'

// ─── Network Fetch ───────────────────────────────────────────────────────────

export const useKrakenNetworkFetch = () => {
  const fetchNetwork = useCallback(
    async (vertexId: string) => {
      return getNetworkFromVertex(vertexId)
    }, [],
  )

  return { fetchNetwork }
}

// ─── Chat Stream ─────────────────────────────────────────────────────────────

export const useKrakenChatStream = () => {
  const streamChat = useCallback(
    async (data: KrakenChatRequest, onEvent: (event: KrakenStreamEvent) => void, signal?: AbortSignal) => {
      return chatWithKrakenStream(data, onEvent, signal)
    },
    [],
  )

  return { streamChat }
}

// ─── Node Expansion Stream ───────────────────────────────────────────────────

export const useKrakenExpandNode = () => {
  const expandNode = useCallback(
    async (vertexId: string, onEvent: (event: KrakenExpandEvent) => void) => {
      return expandNodeStream(vertexId, onEvent)
    },
    [],
  )

  return { expandNode }
}

// ─── Web Presence Discovery Stream ───────────────────────────────────────────

export const useKrakenDiscoverWebPresence = () => {
  const discoverWeb = useCallback(
    async (
      vertexId: string,
      opts: {
        url?: string
        name?: string
        layers?: number
        categories?: string[]
        conversationId?: string
      },
      onEvent: (event: KrakenDiscoverEvent) => void,
    ) => {
      return discoverWebPresenceStream(vertexId, opts, onEvent)
    },
    [],
  )

  return { discoverWeb }
}

// ─── Create Entity with AI (direct, no chat) ─────────────────────────────────

export const useKrakenCreateEntity = () => {
  const createEntity = useCallback(
    async (
      vertexId: string,
      opts: CreateEntityFromNodeOpts,
      onEvent: (event: KrakenCreateEntityEvent) => void,
    ) => {
      return createEntityFromNodeStream(vertexId, opts, onEvent)
    },
    [],
  )

  return { createEntity }
}

// ─── Apply Entity Suggestions ────────────────────────────────────────────────

export const useKrakenApplyEntitySuggestions = () => {
  const applySuggestions = useCallback(
    async (
      entityType: KrakenEntityUpdateType,
      entityId: string,
      suggestions: KrakenEntitySuggestion[],
      appendField?: string,
    ) => {
      return applyEntitySuggestions(entityType, entityId, suggestions, appendField)
    },
    [],
  )

  return { applySuggestions }
}

// ─── O2O Cluster Hooks (KrakenO2O) ───────────────────────────────────────────

export const CLUSTER_KEYS = {
  detail: (id: string) => ['clusterDetail', id] as const,
  list: (params?: ClusterListParams) => ['clusterList', params] as const,
  searchHistory: () => ['clustersSearchHistory'] as const,
}

export const useGetClusterDetail = (id: string | undefined, options?: { enabled?: boolean; refetchInterval?: number | false }) => {
  return useQuery({
    queryKey: CLUSTER_KEYS.detail(id ?? ''),
    queryFn: () => getClusterDetail(id!, { expand: 'nodes.seller,nodes.item,nodes.saw,edges' }),
    enabled: !!id && (options?.enabled ?? true),
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchInterval: options?.refetchInterval,
    refetchIntervalInBackground: true,
  })
}

export const useGetClusterListRecent = () => {
  return useQuery({
    queryKey: CLUSTER_KEYS.list({ page: 1, page_size: 5, ordering: '-created_at' }),
    queryFn: () => getClusterList({ page: 1, page_size: 5, ordering: '-created_at' }),
    staleTime: 60_000,
  })
}

export const useGetClusterSearchHistory = () => {
  return useInfiniteQuery({
    queryKey: CLUSTER_KEYS.searchHistory(),
    queryFn: ({ pageParam = 1 }) => getClusterList({ fields: 'id,name', page: pageParam as number, page_size: 20 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage?.hasNext && lastPage.nextPage ? lastPage.nextPage : undefined,
  })
}

export const usePostCluster = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateClusterPayload) => postCluster(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLUSTER_KEYS.searchHistory() })
      queryClient.invalidateQueries({ queryKey: ['clusterList'] })
    },
  })
}

export const usePatchCluster = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateClusterPayload }) => patchCluster(id, payload),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: CLUSTER_KEYS.detail(id) })
      queryClient.invalidateQueries({ queryKey: CLUSTER_KEYS.searchHistory() })
    },
  })
}

export const useDeleteCluster = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteCluster(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLUSTER_KEYS.searchHistory() })
      queryClient.invalidateQueries({ queryKey: ['clusterList'] })
    },
  })
}

export const useExpandNode = () => {
  return useMutation({
    mutationFn: ({ clusterId, nodeId }: { clusterId: string; nodeId: string }) => expandNode(clusterId, nodeId),
  })
}
